import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Box, Button } from "@mui/material";
import * as React from "react";

import { SearchField } from "@/shared/components/filters";
import { TriggerForEachPrimitive } from "@/shared/domain/actions/TriggerRecord";
import { ConditionsOperator } from "@/types";

import { TriggerCondition } from "./schema";
import styles from "./TriggerConditionForm.module.css";
import { TriggerConditionInput } from "./TriggerConditionInput";
import { TriggerConditionsList } from "./TriggerConditionsList";

interface TriggerConditionFormProps {
  conditions: TriggerCondition[];
  onConditionsChange: (conditions: TriggerCondition[]) => void;
  operator: ConditionsOperator;
  onOperatorChange: (operator: ConditionsOperator) => void;
  forEachPrimitive: TriggerForEachPrimitive;
  searchFields: SearchField[];
  editable: boolean;
  availableMetadataKeys?: string[];
}

/**
 * Main form component for managing trigger conditions.
 *
 * Features:
 * - Displays list of existing conditions with resource prefix badges
 * - Allows adding new conditions
 * - Allows editing existing conditions
 * - Validates file-scoped conditions against trigger's for_each setting
 * - Manages group operator (AND/OR)
 */
export const TriggerConditionForm: React.FC<TriggerConditionFormProps> = ({
  conditions,
  onConditionsChange,
  operator,
  onOperatorChange,
  forEachPrimitive,
  searchFields,
  editable,
  availableMetadataKeys = [],
}) => {
  const [showInput, setShowInput] = React.useState(false);
  const [conditionToEdit, setConditionToEdit] =
    React.useState<TriggerCondition | null>(null);

  const handleCreate = (condition: TriggerCondition) => {
    onConditionsChange([...conditions, condition]);
    setShowInput(false);
  };

  const handleUpdate = (updatedCondition: TriggerCondition) => {
    const newConditions = conditions.map((c) =>
      c.id === updatedCondition.id ? updatedCondition : c,
    );
    onConditionsChange(newConditions);
    setConditionToEdit(null);
    setShowInput(false);
  };

  const handleDelete = (condition: TriggerCondition) => {
    onConditionsChange(conditions.filter((c) => c.id !== condition.id));
  };

  const handleEdit = (condition: TriggerCondition) => {
    setConditionToEdit(condition);
    setShowInput(true);
  };

  const handleCancel = () => {
    setShowInput(false);
    setConditionToEdit(null);
  };

  return (
    <Box>
      {/* Display existing conditions */}
      {conditions.length > 0 && (
        <TriggerConditionsList
          conditions={conditions}
          operator={operator}
          onOperatorChange={onOperatorChange}
          onDeleteCondition={handleDelete}
          onEditCondition={handleEdit}
          editable={editable}
          // Props for inline editing
          conditionToEdit={conditionToEdit}
          showInput={showInput}
          searchFields={searchFields}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onCancel={handleCancel}
          forEachPrimitive={forEachPrimitive}
          availableMetadataKeys={availableMetadataKeys}
        />
      )}

      {/* Add condition form (only shown when adding new, not editing existing) */}
      {showInput && !conditionToEdit && editable && (
        <Box className={styles.sectionSpacing}>
          <TriggerConditionInput
            searchFields={searchFields}
            conditionToEdit={conditionToEdit}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            onCancel={handleCancel}
            forEachPrimitive={forEachPrimitive}
            availableMetadataKeys={availableMetadataKeys}
          />
        </Box>
      )}

      {/* Add Condition button */}
      {!showInput && editable && (
        <Box className={styles.sectionSpacing}>
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setShowInput(true)}
            data-cy="add-condition-button"
          >
            Add Condition
          </Button>
        </Box>
      )}
    </Box>
  );
};
